/**
 * §F8 catalogue — pure client-side filtering over the one `/datasets` payload.
 *
 * No React and no fetching here: `apps.datasets.catalogue.list_datasets` answers the
 * *whole* catalogue in one request (its module docstring: "one request for the whole
 * catalogue"), and the search box, the domain/type/classification rail and the URL's
 * `?q=&domain=&type=&cls=` are all a view over that one array. Keeping the filtering pure
 * means the rail's counts, the search match and the permalink round-trip are each a plain
 * function of data in, data out — testable without a component, a query client or a DOM.
 */
import type {
  DatasetDataType,
  DatasetDomainFacet,
  DatasetFacetCount,
  DatasetFacets,
  DatasetRow,
} from '../../lib/api';
import type { IconName } from '../../lib/icons';
import { NO_VALUE } from '../../lib/format';

/**
 * The five axes the §F8 rail filters on. `domain`/`type`/`cls`/`src` are OR-within,
 * AND-across.
 *
 * `src` is the free-text `source_org` (client, 2026-09-16: *"check also by Data Source —
 * MEL, Baseline Upload, ISRIC etc."*). The server can narrow it too (`?source=`), but only
 * to one organisation; keeping it here with the others is what lets a reader tick both
 * "ISRIC" and "IFDC MEL team", and keeps the whole rail one pass over one payload.
 */
export interface CatalogueFilters {
  q: string;
  domain: Set<string>;
  type: Set<string>;
  cls: Set<string>;
  src: Set<string>;
}

/** No search text and no facet narrowed — the catalogue's cold-boot state. */
export const EMPTY_FILTERS: CatalogueFilters = {
  q: '',
  domain: new Set(),
  type: new Set(),
  cls: new Set(),
  src: new Set(),
};

/** Does `row` belong to the group narrowed by `selected`? An empty set is no constraint. */
function matchesGroup(selected: Set<string>, value: string): boolean {
  return selected.size === 0 || selected.has(value);
}

/**
 * `rows` narrowed to `f`: `q` matches title or abstract case-insensitively, and the three
 * facet groups AND together while each one ORs its own selected values — ticking both
 * `raster` and `vector` widens the type column, but ticking a type *and* a classification
 * narrows to rows that satisfy both.
 */
export function applyFilters(rows: DatasetRow[], f: CatalogueFilters): DatasetRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((row) => {
    if (q && !row.title.toLowerCase().includes(q) && !row.abstract.toLowerCase().includes(q)) {
      return false;
    }
    if (!matchesGroup(f.domain, row.domain ?? '')) return false;
    if (!matchesGroup(f.type, row.data_type)) return false;
    if (!matchesGroup(f.cls, row.classification)) return false;
    if (!matchesGroup(f.src, row.source_org)) return false;
    return true;
  });
}

/**
 * The catalogue header's sort (client, 2026-09-16 — *"make the Data catalogue highly
 * professional, it looks cluttered"*): the grid reads newest-first by default, because a
 * catalogue is read for what changed, and A→Z when the reader is looking one row up.
 */
export type CatalogueSort = 'updated' | 'title';

export const DEFAULT_SORT: CatalogueSort = 'updated';

/**
 * `rows` ordered for the header's Sort select — a **copy**, never the argument in place
 * (the array belongs to the react-query cache).
 *
 * `updated` is descending on `updated_at`, ties broken by title so the order is total and
 * the grid cannot reshuffle between renders; `title` is `localeCompare` in the interface
 * language so French accents sort where a French reader expects rather than after `z`.
 */
export function sortRows(
  rows: DatasetRow[],
  sort: CatalogueSort,
  language?: string,
): DatasetRow[] {
  const byTitle = (a: DatasetRow, b: DatasetRow) => a.title.localeCompare(b.title, language);
  return [...rows].sort((a, b) =>
    sort === 'title' ? byTitle(a, b) : b.updated_at.localeCompare(a.updated_at) || byTitle(a, b),
  );
}

/**
 * Organisations the opening-letters rule cannot tell apart, spelled out.
 *
 * "IFDC" and "IFDC MEL team" are different publishers — one is the institute, the other the
 * programme's own monitoring team — and the rule reads both as "IFDC", which is exactly the
 * confusion a monogram exists to prevent. Keyed on the whole `source_org` so it is an exact
 * decision, not a pattern that will surprise the next organisation added to the seed.
 */
const MONOGRAM_OVERRIDES: Record<string, string> = {
  'IFDC MEL team': 'MEL',
  'ISRIC + IFDC': 'ISR',
  'WUR + IFDC': 'WUR',
};

/**
 * The tile monogram a tabular row wears where a raster row has a picture — the opening
 * letters of its `source_org`, uppercased: "MEL", "ISR", "IFDC", "ESA".
 *
 * The first *word*, not the first three characters of the whole string: "IFDC MEL team" is
 * "IFDC" and not "IFD", and "ESA / Copernicus" is "ESA" and not "ESA" running into the
 * slash. A word longer than four letters is cut to three ("ISRIC" → "ISR") — four is where
 * a monogram stops reading as one and starts reading as a truncation. An empty or
 * punctuation-only source answers `''` and the tile draws nothing rather than a stray glyph.
 */
export function sourceMonogram(sourceOrg: string): string {
  const override = MONOGRAM_OVERRIDES[sourceOrg];
  if (override) return override;
  const word = sourceOrg.match(/[\p{L}\p{N}]+/u)?.[0] ?? '';
  return (word.length <= 4 ? word : word.slice(0, 3)).toUpperCase();
}

/**
 * What the no-picture tile actually prints: the source monogram, or the domain's own short
 * code when the source is too short to be one, or nothing at all.
 *
 * A one- or two-letter `source_org` is not a monogram, it is a stray glyph on a 16:10 tile —
 * so a row like that borrows its domain's own opening letters under the same rule ("SOIL"
 * from "Soil health", "WAT" from "Water resources") instead. A row with neither keeps the
 * tile and prints nothing on it: the box's neutral ground is still a tile, not a blank.
 */
export function tileMonogram(sourceOrg: string, domainName?: string): string {
  const source = sourceMonogram(sourceOrg);
  if (source.length >= 3) return source;
  return sourceMonogram(domainName ?? '') || source;
}

/**
 * The BCP 47 tag each interface language formats dates in.
 *
 * `en` alone resolves to `en-US` in most runtimes, which prints "Sep 16, 2026" — an order
 * this product's own English never uses (it writes *organisation*, *licence*, *programme*).
 * Naming `en-GB` keeps the card's date reading "16 Sep 2026" wherever it is rendered, and
 * `fr-FR` keeps the French card on "16 sept. 2026".
 */
const DATE_LOCALES: Record<string, string> = { fr: 'fr-FR', en: 'en-GB' };

/**
 * `en-GB`'s own `month: 'short'` is **four** letters for September ("Sept"), alone among the
 * twelve — so a column of card dates reads Jan/Feb/…/Sept/Oct and one row is a character
 * wider than the rest for no reason a reader can name. English months are cut back to the
 * three-letter abbreviation every other month already uses; French is left exactly as
 * `fr-FR` writes it, because "sept." *is* the French abbreviation and "sep" is not a word.
 */
function trimEnglishMonth(parts: Intl.DateTimeFormatPart[]): Intl.DateTimeFormatPart[] {
  return parts.map((part) =>
    part.type === 'month' ? { ...part, value: part.value.replace(/\.$/, '').slice(0, 3) } : part,
  );
}

/**
 * `2026-09-16T…` → "16 Sep 2026" in `language`, or `NO_VALUE` when the row has no date.
 *
 * `updated_at` is a date-only or full ISO string from the server; slicing to the date part
 * and building the `Date` from its three numbers keeps a bare `2026-09-16` from being read
 * as UTC midnight and printed as the 15th west of Greenwich.
 */
export function formatUpdated(iso: string | null | undefined, language: string): string {
  const ymd = (iso ?? '').slice(0, 10).split('-').map(Number);
  if (ymd.length !== 3 || !ymd.every((n) => Number.isFinite(n))) return NO_VALUE;
  const [y, m, d] = ymd as [number, number, number];
  const lang = language.slice(0, 2);
  const locale = DATE_LOCALES[lang] ?? 'en-GB';
  const parts = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).formatToParts(new Date(y, m - 1, d));
  return (lang === 'fr' ? parts : trimEnglishMonth(parts)).map((part) => part.value).join('');
}

/**
 * How many source options the rail shows before "Show all" — the catalogue carries 27
 * organisations and a rail that lists them all is a scroll, not a filter.
 */
export const SOURCE_FACET_LIMIT = 8;

/**
 * The source options to draw while the group is collapsed: the `limit` largest by count,
 * **plus** every option the reader has already ticked, in the payload's own (label) order.
 *
 * Keeping the payload's order rather than re-sorting by count means expanding the group
 * adds rows without moving the ones already on screen. Keeping a ticked option visible
 * means a filter can always be seen — and switched off — without expanding anything, which
 * matters most for the long tail the limit exists to hide.
 */
export function visibleSourceFacets<T extends { id: string; count: number }>(
  facets: T[],
  selected: Set<string>,
  limit = SOURCE_FACET_LIMIT,
): T[] {
  const top = new Set(
    [...facets]
      .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id))
      .slice(0, limit)
      .map((f) => f.id),
  );
  return facets.filter((f) => top.has(f.id) || selected.has(f.id));
}

/** A facet's own key on `DatasetRow` — what `facetCounts` groups the unfiltered rows by. */
export type FacetKey = 'domain' | 'data_type' | 'classification' | 'source_org';

/**
 * How many of the *unfiltered* `rows` carry each value of `key`. Always over the whole
 * catalogue, never over what the caller has already narrowed to — the §F8 rail keeps every
 * option's count fixed while the list beside it shrinks, mirroring the server's own
 * `apps.datasets.catalogue.facets` (which the list envelope already answers over the whole
 * catalogue for the same reason); this is the client-side rebuild of the same number when a
 * component only has `rows`, not a fresh `/datasets` round trip, to count from.
 */
export function facetCounts(rows: DatasetRow[], key: FacetKey): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value = key === 'domain' ? (row.domain ?? '') : row[key];
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return counts;
}

/** `,`-joined set → sorted array, for a stable, order-independent serialisation. */
function toSortedArray(set: Set<string>): string[] {
  return [...set].sort();
}

/** `?q=&domain=a,b&type=&cls=&src=` → `CatalogueFilters`. Absent/empty params read as no filter. */
export function parseCatalogueSearch(search: string): CatalogueFilters {
  const sp = new URLSearchParams(search);
  const toSet = (name: string): Set<string> => {
    const raw = sp.get(name);
    return raw ? new Set(raw.split(',').filter(Boolean)) : new Set<string>();
  };
  return {
    q: sp.get('q') ?? '',
    domain: toSet('domain'),
    type: toSet('type'),
    cls: toSet('cls'),
    src: toSet('src'),
  };
}

/**
 * `CatalogueFilters` → `?q=&domain=a,b&type=&cls=&src=`, omitting empty params entirely —
 * `serialiseCatalogueSearch(EMPTY_FILTERS)` is `''`, so the cold-boot state leaves no trace
 * on the URL.
 */
export function serialiseCatalogueSearch(f: CatalogueFilters): string {
  const sp = new URLSearchParams();
  if (f.q) sp.set('q', f.q);
  if (f.domain.size) sp.set('domain', toSortedArray(f.domain).join(','));
  if (f.type.size) sp.set('type', toSortedArray(f.type).join(','));
  if (f.cls.size) sp.set('cls', toSortedArray(f.cls).join(','));
  if (f.src.size) sp.set('src', toSortedArray(f.src).join(','));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * The `facets.domain` array as an `id → facet` lookup.
 *
 * Two callers need the same map from the same source: the card grid, which tints and names
 * a card's domain pill, and `app/RecordHost.tsx`, which hands it to `DatasetRecord` as
 * `domainNames`. Deriving it once here is what keeps a record's domain row identical
 * whether the reader opened it from a catalogue card or from the map's layer list.
 */
export function domainFacetsById(
  facets: DatasetDomainFacet[] | undefined,
): Map<string, DatasetDomainFacet> {
  return new Map((facets ?? []).map((d) => [d.id, d]));
}


/* ---------- The hero band and the grid/list switch (client, 2026-09-16) ---------- */
/*
 * *"still not happy with our modernity and creativity on the catalogue"*. Three pure
 * helpers back the new header: `typeCounts` (the subtitle's "57 rasters · 21 vectors ·
 * 17 tables"), `quickChips` (the "Browse by" row) and `readCatView`/`writeCatView` (which
 * of the two layouts the reader last chose). All three read the *server's* facets, which
 * are computed over the whole catalogue — so the hero keeps saying what the catalogue
 * holds while the grid under it narrows, which is the only thing a header of this kind is
 * for.
 */

/** The three §D4 `data_type` values, in the order the rail and the hero both name them. */
export const DATA_TYPES: DatasetDataType[] = ['raster', 'vector', 'tabular'];

/** The glyph a card, a list row and a chip all wear for a given `data_type`. */
const TYPE_ICONS: Record<DatasetDataType, IconName> = {
  raster: 'raster',
  vector: 'vector',
  tabular: 'table',
};

/** `row.data_type` -> its `lib/icons` name. Total over the closed vocabulary, so no fallback. */
export function dataTypeIcon(type: DatasetDataType): IconName {
  return TYPE_ICONS[type] ?? 'table';
}

/**
 * `facets.data_type` as a complete record — every one of the three keys present, `0` where
 * the server counted none.
 *
 * The payload's array carries only the types the catalogue actually holds and in no fixed
 * order, and the hero's subtitle names all three in a fixed one. Answering a total record
 * is what lets the caller write `counts.raster` without a `?? 0` at every use.
 */
export function typeCounts(facets: DatasetFacetCount[] | undefined): Record<DatasetDataType, number> {
  const byId = new Map((facets ?? []).map((f) => [f.id, f.count]));
  return {
    raster: byId.get('raster') ?? 0,
    vector: byId.get('vector') ?? 0,
    tabular: byId.get('tabular') ?? 0,
  };
}

/** Which rail group a "Browse by" chip toggles — the same four `CatalogueFilters` keys. */
export type QuickChipGroup = 'type' | 'cls' | 'src';

/**
 * One chip in the hero's "Browse by" row. `key` is both the React key and the chip's own
 * `data-testid` suffix (`cat-chip-<key>`); `label` is set only for a source chip, whose
 * name is a free-text organisation and so is not a translation key.
 */
export interface QuickChip {
  key: string;
  group: QuickChipGroup;
  value: string;
  label?: string;
  count: number;
}

/** How many organisations the "Browse by" row offers. The rail has all 27; this is a shortcut. */
export const QUICK_CHIP_SOURCES = 3;

/**
 * The hero's "Browse by" row: the three data types, Restricted, then the `limit` largest
 * sources by count.
 *
 * A chip the server counted **zero** of is dropped rather than drawn dead — offering
 * "Vectors" on a catalogue with no vector in it is a filter that can only ever empty the
 * grid. Sources sort by count descending and break ties on the id, so the row is stable
 * between reads of the same catalogue; they keep their own `label` because an organisation
 * is not a translated concept (the rail's own Data source group says the same).
 */
export function quickChips(
  facets: DatasetFacets | undefined,
  limit = QUICK_CHIP_SOURCES,
): QuickChip[] {
  const counts = typeCounts(facets?.data_type);
  const restricted =
    (facets?.classification ?? []).find((f) => f.id === 'restricted')?.count ?? 0;
  const chips: QuickChip[] = [
    ...DATA_TYPES.map((id) => ({ key: id, group: 'type' as const, value: id, count: counts[id] })),
    { key: 'restricted', group: 'cls' as const, value: 'restricted', count: restricted },
    ...[...(facets?.source ?? [])]
      .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id))
      .slice(0, limit)
      .map((f) => ({
        key: `src-${f.id}`,
        group: 'src' as const,
        value: f.id,
        label: f.label ?? f.id,
        count: f.count,
      })),
  ];
  return chips.filter((c) => c.count > 0);
}

/** The two catalogue layouts. `grid` is the cold-boot default — it always has been. */
export type CatalogueView = 'grid' | 'list';

export const DEFAULT_VIEW: CatalogueView = 'grid';

/**
 * Where the reader's choice of layout lives. A preference, not view state: it says how this
 * reader likes to read a catalogue, so it belongs in `localStorage` beside `sv.splashSeen`
 * and not in the `?q=&domain=…` link they hand to somebody else.
 */
export const CAT_VIEW_KEY = 'sv.catView';

/** The stored layout, or `grid` — for a first visit, a junk value, or a blocked store. */
export function readCatView(): CatalogueView {
  try {
    return window.localStorage.getItem(CAT_VIEW_KEY) === 'list' ? 'list' : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
}

/** Remember the layout. A store that throws (private mode, blocked cookies) is not an error. */
export function writeCatView(view: CatalogueView): void {
  try {
    window.localStorage.setItem(CAT_VIEW_KEY, view);
  } catch {
    /* preference only — a reader with no writable store simply starts on the grid. */
  }
}
